import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './TeacherSections.css';

const TeacherSectionCourseLibraryManagement = () => {
  const { t } = useTranslation('teacher');
  // State for course library management
  const [activeTab, setActiveTab] = useState('overview');
  const [uploadModal, setUploadModal] = useState(false);
  const [editModal, setEditModal] = useState({ show: false, content: null });
  const [previewModal, setPreviewModal] = useState({ show: false, content: null });
  const [analyticsModal, setAnalyticsModal] = useState({ show: false, content: null });
  const [selectedContent, setSelectedContent] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('date-created');
  const [viewMode, setViewMode] = useState('grid');

  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    category: '',
    subcategory: '',
    difficulty: 'beginner',
    language: 'english',
    tags: '',
    price: 0,
    isFree: true,
    thumbnail: null,
    contentFiles: [],
    videoUrl: '',
    audioUrl: '',
    pdfUrl: '',
    duration: '',
    learningObjectives: '',
    prerequisites: '',
    targetAudience: '',
    certificate: false,
    certificateName: '',
    maxStudents: 0,
    startDate: '',
    endDate: '',
    isPublished: false,
    isFeatured: false,
    seoTitle: '',
    seoDescription: '',
    seoKeywords: ''
  });

  // Sample content data
  const [myContent, setMyContent] = useState([
    {
      id: 1,
      title: 'Advanced English Grammar Mastery',
      description: 'Comprehensive course covering advanced grammar concepts with practical exercises',
      category: 'Grammar',
      subcategory: 'Advanced',
      difficulty: 'advanced',
      language: 'english',
      status: 'published',
      views: 1247,
      enrollments: 89,
      rating: 4.8,
      reviews: 23,
      revenue: 1247.50,
      dateCreated: '2024-01-15',
      lastUpdated: '2024-03-10',
      thumbnail: '/thumbnails/grammar-course.jpg',
      duration: '8 hours',
      price: 29.99,
      isFree: false,
      isFeatured: true,
      certificate: true,
      tags: ['grammar', 'advanced', 'english', 'writing']
    },
    {
      id: 2,
      title: 'IELTS Speaking Practice Sessions',
      description: 'Interactive speaking practice sessions for IELTS preparation',
      category: 'Speaking',
      subcategory: 'IELTS',
      difficulty: 'intermediate',
      language: 'english',
      status: 'draft',
      views: 0,
      enrollments: 0,
      rating: 0,
      reviews: 0,
      revenue: 0,
      dateCreated: '2024-03-12',
      lastUpdated: '2024-03-12',
      thumbnail: '/thumbnails/speaking-course.jpg',
      duration: '6 hours',
      price: 19.99,
      isFree: false,
      isFeatured: false,
      certificate: true,
      tags: ['speaking', 'ielts', 'practice', 'english']
    },
    {
      id: 3,
      title: 'Business English Vocabulary',
      description: 'Essential business vocabulary and phrases for professional communication',
      category: 'Vocabulary',
      subcategory: 'Business',
      difficulty: 'intermediate',
      language: 'english',
      status: 'published',
      views: 892,
      enrollments: 45,
      rating: 4.6,
      reviews: 18,
      revenue: 675.50,
      dateCreated: '2024-02-20',
      lastUpdated: '2024-03-05',
      thumbnail: '/thumbnails/business-vocab.jpg',
      duration: '4 hours',
      price: 14.99,
      isFree: false,
      isFeatured: false,
      certificate: false,
      tags: ['business', 'vocabulary', 'professional', 'english']
    }
  ]);

  const [analytics, setAnalytics] = useState({
    totalContent: 15,
    publishedContent: 12,
    draftContent: 3,
    totalViews: 3456,
    totalEnrollments: 234,
    totalRevenue: 3247.50,
    averageRating: 4.7,
    monthlyGrowth: 12.5,
    topPerformingContent: 'Advanced English Grammar Mastery',
    recentActivity: [
      { type: 'enrollment', content: 'Advanced English Grammar Mastery', student: 'Sarah J.', time: '2 hours ago' },
      { type: 'review', content: 'Business English Vocabulary', student: 'Mike C.', rating: 5, time: '4 hours ago' },
      { type: 'view', content: 'IELTS Speaking Practice Sessions', time: '6 hours ago' }
    ]
  });

  const categories = [
    'Grammar', 'Vocabulary', 'Speaking', 'Listening', 'Reading', 'Writing', 
    'Pronunciation', 'Business English', 'Academic English', 'Conversation'
  ];

  const difficulties = ['beginner', 'intermediate', 'advanced', 'expert'];
  const languages = ['english', 'spanish', 'french', 'german', 'chinese', 'japanese'];
  const statuses = ['draft', 'published', 'archived', 'pending'];

  const handleUploadInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    if (type === 'file') {
      if (name === 'thumbnail') {
        setUploadForm(prev => ({
          ...prev,
          thumbnail: files[0]
        }));
      } else if (name === 'contentFiles') {
        setUploadForm(prev => ({
          ...prev,
          contentFiles: Array.from(files)
        }));
      }
    } else if (type === 'checkbox') {
      setUploadForm(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setUploadForm(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleUploadSubmit = (e) => {
    e.preventDefault();
    
    // Create new content item
    const newContent = {
      id: Date.now(),
      ...uploadForm,
      status: uploadForm.isPublished ? 'published' : 'draft',
      views: 0,
      enrollments: 0,
      rating: 0,
      reviews: 0,
      revenue: 0,
      dateCreated: new Date().toISOString().split('T')[0],
      lastUpdated: new Date().toISOString().split('T')[0],
      tags: uploadForm.tags.split(',').map(tag => tag.trim())
    };
    
    setMyContent(prev => [newContent, ...prev]);
    
    // Reset form
    setUploadForm({
      title: '',
      description: '',
      category: '',
      subcategory: '',
      difficulty: 'beginner',
      language: 'english',
      tags: '',
      price: 0,
      isFree: true,
      thumbnail: null,
      contentFiles: [],
      videoUrl: '',
      audioUrl: '',
      pdfUrl: '',
      duration: '',
      learningObjectives: '',
      prerequisites: '',
      targetAudience: '',
      certificate: false,
      certificateName: '',
      maxStudents: 0,
      startDate: '',
      endDate: '',
      isPublished: false,
      isFeatured: false,
      seoTitle: '',
      seoDescription: '',
      seoKeywords: ''
    });
    
    setUploadModal(false);
  };

  const handleEditContent = (content) => {
    setEditModal({ show: true, content });
  };

  const handleDeleteContent = (contentId) => {
    if (window.confirm('Are you sure you want to delete this content? This action cannot be undone.')) {
      setMyContent(prev => prev.filter(content => content.id !== contentId));
    }
  };

  const handlePublishContent = (contentId) => {
    setMyContent(prev => prev.map(content => 
      content.id === contentId 
        ? { ...content, status: 'published' }
        : content
    ));
  };

  const handleUnpublishContent = (contentId) => {
    setMyContent(prev => prev.map(content => 
      content.id === contentId 
        ? { ...content, status: 'draft' }
        : content
    ));
  };

  const handleDuplicateContent = (content) => {
    const duplicatedContent = {
      ...content,
      id: Date.now(),
      title: `${content.title} (Copy)`,
      status: 'draft',
      views: 0,
      enrollments: 0,
      rating: 0,
      reviews: 0,
      revenue: 0,
      dateCreated: new Date().toISOString().split('T')[0],
      lastUpdated: new Date().toISOString().split('T')[0]
    };
    
    setMyContent(prev => [duplicatedContent, ...prev]);
  };

  const handleBulkAction = (action) => {
    if (selectedContent.length === 0) {
      alert('Please select content to perform bulk actions');
      return;
    }

    switch (action) {
      case 'publish':
        setMyContent(prev => prev.map(content => 
          selectedContent.includes(content.id) 
            ? { ...content, status: 'published' }
            : content
        ));
        break;
      case 'unpublish':
        setMyContent(prev => prev.map(content => 
          selectedContent.includes(content.id) 
            ? { ...content, status: 'draft' }
            : content
        ));
        break;
      case 'delete':
        if (window.confirm(`Are you sure you want to delete ${selectedContent.length} items?`)) {
          setMyContent(prev => prev.filter(content => !selectedContent.includes(content.id)));
        }
        break;
      case 'duplicate':
        const contentToDuplicate = myContent.filter(content => selectedContent.includes(content.id));
        const duplicatedContent = contentToDuplicate.map(content => ({
          ...content,
          id: Date.now() + Math.random(),
          title: `${content.title} (Copy)`,
          status: 'draft',
          views: 0,
          enrollments: 0,
          rating: 0,
          reviews: 0,
          revenue: 0,
          dateCreated: new Date().toISOString().split('T')[0],
          lastUpdated: new Date().toISOString().split('T')[0]
        }));
        setMyContent(prev => [...duplicatedContent, ...prev]);
        break;
    }
    setSelectedContent([]);
  };

  const filteredContent = myContent.filter(content => {
    const matchesSearch = content.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         content.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !categoryFilter || content.category === categoryFilter;
    const matchesStatus = !statusFilter || content.status === statusFilter;
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const sortedContent = [...filteredContent].sort((a, b) => {
    switch (sortBy) {
      case 'date-created':
        return new Date(b.dateCreated) - new Date(a.dateCreated);
      case 'date-updated':
        return new Date(b.lastUpdated) - new Date(a.lastUpdated);
      case 'title':
        return a.title.localeCompare(b.title);
      case 'views':
        return b.views - a.views;
      case 'enrollments':
        return b.enrollments - a.enrollments;
      case 'rating':
        return b.rating - a.rating;
      case 'revenue':
        return b.revenue - a.revenue;
      default:
        return 0;
    }
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'published': return 'success';
      case 'draft': return 'warning';
      case 'archived': return 'error';
      case 'pending': return 'info';
      default: return 'default';
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'success';
      case 'intermediate': return 'warning';
      case 'advanced': return 'error';
      case 'expert': return 'accent';
      default: return 'default';
    }
  };

  return (
    <div className="teacher-main-content">
      <div className="teacher-content-header">
        <h1 className="teacher-content-title">{t('courseLibraryManagement.title')}</h1>
        <p className="teacher-content-subtitle">{t('courseLibraryManagement.subtitle')}</p>
      </div>

      {/* Analytics Overview */}
      <div className="teacher-section">
        <div className="teacher-section-header">
          <div className="teacher-section-title">
            <i className="material-icons">analytics</i>
            {t('courseLibraryManagement.contentPerformanceOverview')}
          </div>
          <button 
            className="teacher-btn secondary"
            onClick={() => setAnalyticsModal({ show: true, content: null })}
          >
            <i className="material-icons">insights</i>
            {t('courseLibraryManagement.detailedAnalytics')}
          </button>
        </div>

        <div className="teacher-overview-grid">
          <div className="teacher-overview-card">
            <div className="teacher-overview-header">
              <span className="teacher-overview-title">{t('courseLibraryManagement.totalContent')}</span>
              <div className="teacher-overview-icon">
                <i className="material-icons">library_books</i>
              </div>
            </div>
            <div className="teacher-overview-value">{analytics.totalContent}</div>
            <div className="teacher-overview-change">
              <i className="material-icons">trending_up</i>
              +{analytics.monthlyGrowth}% {t('courseLibraryManagement.thisMonth')}
            </div>
          </div>

          <div className="teacher-overview-card success">
            <div className="teacher-overview-header">
              <span className="teacher-overview-title">{t('courseLibraryManagement.totalViews')}</span>
              <div className="teacher-overview-icon">
                <i className="material-icons">visibility</i>
              </div>
            </div>
            <div className="teacher-overview-value">{analytics.totalViews.toLocaleString()}</div>
            <div className="teacher-overview-change">
              <i className="material-icons">trending_up</i>
              +15% {t('courseLibraryManagement.thisWeek')}
            </div>
          </div>

          <div className="teacher-overview-card accent">
            <div className="teacher-overview-header">
              <span className="teacher-overview-title">{t('courseLibraryManagement.totalRevenue')}</span>
              <div className="teacher-overview-icon">
                <i className="material-icons">attach_money</i>
              </div>
            </div>
            <div className="teacher-overview-value">${analytics.totalRevenue.toLocaleString()}</div>
            <div className="teacher-overview-change">
              <i className="material-icons">trending_up</i>
              +8% {t('courseLibraryManagement.thisMonth')}
            </div>
          </div>

          <div className="teacher-overview-card warning">
            <div className="teacher-overview-header">
              <span className="teacher-overview-title">{t('courseLibraryManagement.averageRating')}</span>
              <div className="teacher-overview-icon">
                <i className="material-icons">star</i>
              </div>
            </div>
            <div className="teacher-overview-value">{analytics.averageRating}</div>
            <div className="teacher-overview-change">
              <i className="material-icons">trending_up</i>
              +0.2 {t('courseLibraryManagement.thisMonth')}
            </div>
          </div>
        </div>
      </div>

              {/* Content Management Tabs */}
        <div className="teacher-section">
          <div className="teacher-section-header">
            <div className="teacher-tabs">
              <button 
                className={`teacher-tab ${activeTab === 'overview' ? 'active' : ''}`}
                onClick={() => setActiveTab('overview')}
              >
                <i className="material-icons">dashboard</i>
                {t('courseLibraryManagement.overview')}
              </button>
              <button 
                className={`teacher-tab ${activeTab === 'my-content' ? 'active' : ''}`}
                onClick={() => setActiveTab('my-content')}
              >
                <i className="material-icons">library_books</i>
                {t('courseLibraryManagement.myContent')}
              </button>
              <button 
                className={`teacher-tab ${activeTab === 'analytics' ? 'active' : ''}`}
                onClick={() => setActiveTab('analytics')}
              >
                <i className="material-icons">analytics</i>
                {t('courseLibraryManagement.analytics')}
              </button>
              <button 
                className={`teacher-tab ${activeTab === 'templates' ? 'active' : ''}`}
                onClick={() => setActiveTab('templates')}
              >
                <i className="material-icons">content_copy</i>
                {t('courseLibraryManagement.templates')}
              </button>
              <button 
                className={`teacher-tab ${activeTab === 'resources' ? 'active' : ''}`}
                onClick={() => setActiveTab('resources')}
              >
                <i className="material-icons">folder</i>
                {t('courseLibraryManagement.resources')}
              </button>
              <button 
                className={`teacher-tab ${activeTab === 'settings' ? 'active' : ''}`}
                onClick={() => setActiveTab('settings')}
              >
                <i className="material-icons">settings</i>
                {t('courseLibraryManagement.settings')}
              </button>
            </div>
            
            <div className="teacher-header-actions">
              <button 
                className="teacher-btn primary" 
                onClick={() => setUploadModal(true)}
              >
                <i className="material-icons">add</i>
                {t('courseLibraryManagement.uploadContent')}
              </button>
            </div>
          </div>

        {/* Search and Filters */}
        <div className="teacher-filters">
          <div className="teacher-search">
            <i className="material-icons">search</i>
            <input
              type="text"
              placeholder={t('courseLibraryManagement.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="teacher-filter-group">
            <select 
              value={categoryFilter} 
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="teacher-select"
            >
              <option value="">{t('courseLibraryManagement.allCategories')}</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>

            <select 
              value={statusFilter} 
              onChange={(e) => setStatusFilter(e.target.value)}
              className="teacher-select"
            >
              <option value="">{t('courseLibraryManagement.allStatus')}</option>
              {statuses.map(status => (
                <option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</option>
              ))}
            </select>

            <select 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
              className="teacher-select"
            >
              <option value="date-created">{t('courseLibraryManagement.dateCreated')}</option>
              <option value="date-updated">{t('courseLibraryManagement.lastUpdated')}</option>
              <option value="title">Title</option>
              <option value="views">Most Views</option>
              <option value="enrollments">Most Enrollments</option>
              <option value="rating">Highest Rating</option>
              <option value="revenue">Highest Revenue</option>
            </select>

            <div className="teacher-view-toggle">
              <button 
                className={`teacher-view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
              >
                <i className="material-icons">grid_view</i>
              </button>
              <button 
                className={`teacher-view-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
              >
                <i className="material-icons">list</i>
              </button>
            </div>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedContent.length > 0 && (
          <div className="teacher-bulk-actions">
            <span className="teacher-bulk-count">
              {selectedContent.length} item(s) selected
            </span>
            <div className="teacher-bulk-buttons">
              <button 
                className="teacher-btn secondary"
                onClick={() => handleBulkAction('publish')}
              >
                <i className="material-icons">publish</i>
                Publish
              </button>
              <button 
                className="teacher-btn secondary"
                onClick={() => handleBulkAction('unpublish')}
              >
                <i className="material-icons">unpublish</i>
                Unpublish
              </button>
              <button 
                className="teacher-btn secondary"
                onClick={() => handleBulkAction('duplicate')}
              >
                <i className="material-icons">content_copy</i>
                Duplicate
              </button>
              <button 
                className="teacher-btn error"
                onClick={() => handleBulkAction('delete')}
              >
                <i className="material-icons">delete</i>
                Delete
              </button>
            </div>
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="teacher-overview-section">
            <div className="teacher-overview-grid">
              <div className="teacher-overview-card">
                <div className="teacher-overview-header">
                  <span className="teacher-overview-title">{t('courseLibraryManagement.totalContent')}</span>
                  <div className="teacher-overview-icon">
                    <i className="material-icons">library_books</i>
                  </div>
                </div>
                <div className="teacher-overview-value">{analytics.totalContent}</div>
                <div className="teacher-overview-change">
                  <i className="material-icons">trending_up</i>
                  +{analytics.monthlyGrowth}% {t('courseLibraryManagement.thisMonth')}
                </div>
              </div>

              <div className="teacher-overview-card success">
                <div className="teacher-overview-header">
                  <span className="teacher-overview-title">{t('courseLibraryManagement.publishedContent')}</span>
                  <div className="teacher-overview-icon">
                    <i className="material-icons">publish</i>
                  </div>
                </div>
                <div className="teacher-overview-value">{analytics.publishedContent}</div>
                <div className="teacher-overview-change">
                  <i className="material-icons">check_circle</i>
                  {t('courseLibraryManagement.activeContent')}
                </div>
              </div>

              <div className="teacher-overview-card accent">
                <div className="teacher-overview-header">
                  <span className="teacher-overview-title">{t('courseLibraryManagement.totalRevenue')}</span>
                  <div className="teacher-overview-icon">
                    <i className="material-icons">attach_money</i>
                  </div>
                </div>
                <div className="teacher-overview-value">${analytics.totalRevenue.toLocaleString()}</div>
                <div className="teacher-overview-change">
                  <i className="material-icons">trending_up</i>
                  +8% {t('courseLibraryManagement.thisMonth')}
                </div>
              </div>

              <div className="teacher-overview-card warning">
                <div className="teacher-overview-header">
                  <span className="teacher-overview-title">{t('courseLibraryManagement.averageRating')}</span>
                  <div className="teacher-overview-icon">
                    <i className="material-icons">star</i>
                  </div>
                </div>
                <div className="teacher-overview-value">{analytics.averageRating}</div>
                <div className="teacher-overview-change">
                  <i className="material-icons">trending_up</i>
                  +0.2 {t('courseLibraryManagement.thisMonth')}
                </div>
              </div>
            </div>

            <div className="teacher-recent-activity">
              <h3>{t('courseLibraryManagement.recentActivity')}</h3>
              <div className="teacher-activity-list">
                {analytics.recentActivity.map((activity, index) => (
                  <div key={index} className="teacher-activity-item">
                    <div className="activity-icon">
                      <i className="material-icons">
                        {activity.type === 'enrollment' ? 'school' : 
                         activity.type === 'review' ? 'star' : 'visibility'}
                      </i>
                    </div>
                    <div className="activity-content">
                      <p className="activity-text">
                        {activity.type === 'enrollment' ? `${activity.student} ${t('courseLibraryManagement.enrolledIn')}` :
                         activity.type === 'review' ? `${activity.student} ${t('courseLibraryManagement.rated')}` :
                         `${activity.content} ${t('courseLibraryManagement.wasViewed')}`}
                        <strong> {activity.content}</strong>
                      </p>
                      <span className="activity-time">{activity.time}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* My Content Tab */}
        {activeTab === 'my-content' && (
          <>
            {/* Search and Filters */}
            <div className="teacher-filters">
              <div className="teacher-search">
                <i className="material-icons">search</i>
                <input
                  type="text"
                  placeholder="Search your content..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <div className="teacher-filter-group">
                <select 
                  value={categoryFilter} 
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="teacher-select"
                >
                  <option value="">{t('courseLibraryManagement.allCategories')}</option>
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>

                <select 
                  value={statusFilter} 
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="teacher-select"
                >
                  <option value="">{t('courseLibraryManagement.allStatus')}</option>
                  {statuses.map(status => (
                    <option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</option>
                  ))}
                </select>

                <select 
                  value={sortBy} 
                  onChange={(e) => setSortBy(e.target.value)}
                  className="teacher-select"
                >
                  <option value="date-created">Date Created</option>
                  <option value="date-updated">Last Updated</option>
                  <option value="title">Title</option>
                  <option value="views">Most Views</option>
                  <option value="enrollments">Most Enrollments</option>
                  <option value="rating">Highest Rating</option>
                  <option value="revenue">Highest Revenue</option>
                </select>

                <div className="teacher-view-toggle">
                  <button 
                    className={`teacher-view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                    onClick={() => setViewMode('grid')}
                  >
                    <i className="material-icons">grid_view</i>
                  </button>
                  <button 
                    className={`teacher-view-btn ${viewMode === 'list' ? 'active' : ''}`}
                    onClick={() => setViewMode('list')}
                  >
                    <i className="material-icons">list</i>
                  </button>
                </div>
              </div>
            </div>

            {/* Bulk Actions */}
            {selectedContent.length > 0 && (
              <div className="teacher-bulk-actions">
                <span className="teacher-bulk-count">
                  {selectedContent.length} item(s) selected
                </span>
                <div className="teacher-bulk-buttons">
                  <button 
                    className="teacher-btn secondary"
                    onClick={() => handleBulkAction('publish')}
                  >
                    <i className="material-icons">publish</i>
                    Publish
                  </button>
                  <button 
                    className="teacher-btn secondary"
                    onClick={() => handleBulkAction('unpublish')}
                  >
                    <i className="material-icons">unpublish</i>
                    Unpublish
                  </button>
                  <button 
                    className="teacher-btn secondary"
                    onClick={() => handleBulkAction('duplicate')}
                  >
                    <i className="material-icons">content_copy</i>
                    Duplicate
                  </button>
                  <button 
                    className="teacher-btn error"
                    onClick={() => handleBulkAction('delete')}
                  >
                    <i className="material-icons">delete</i>
                    Delete
                  </button>
                </div>
              </div>
            )}

            {/* Content Grid/List */}
            <div className={`teacher-content-${viewMode}`}>
              {sortedContent.map(content => (
                <div key={content.id} className="teacher-content-card">
                  <div className="teacher-content-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedContent.includes(content.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedContent(prev => [...prev, content.id]);
                        } else {
                          setSelectedContent(prev => prev.filter(id => id !== content.id));
                        }
                      }}
                    />
                  </div>

                  <div className="teacher-content-thumbnail">
                    <img src={content.thumbnail || '/default-thumbnail.jpg'} alt={content.title} />
                    <div className="teacher-content-status">
                      <span className={`teacher-status-badge ${getStatusColor(content.status)}`}>
                        {content.status}
                      </span>
                    </div>
                  </div>

                  <div className="teacher-content-info">
                    <h3 className="teacher-content-title">{content.title}</h3>
                    <p className="teacher-content-description">{content.description}</p>
                    
                    <div className="teacher-content-meta">
                      <span className="teacher-content-category">{content.category}</span>
                      <span className={`teacher-content-difficulty ${getDifficultyColor(content.difficulty)}`}>
                        {content.difficulty}
                      </span>
                      <span className="teacher-content-duration">{content.duration}</span>
                    </div>

                    <div className="teacher-content-stats">
                      <div className="teacher-stat">
                        <i className="material-icons">visibility</i>
                        <span>{content.views}</span>
                      </div>
                      <div className="teacher-stat">
                        <i className="material-icons">school</i>
                        <span>{content.enrollments}</span>
                      </div>
                      <div className="teacher-stat">
                        <i className="material-icons">star</i>
                        <span>{content.rating}</span>
                      </div>
                      <div className="teacher-stat">
                        <i className="material-icons">attach_money</i>
                        <span>${content.revenue}</span>
                      </div>
                    </div>

                    <div className="teacher-content-actions">
                      <button 
                        className="teacher-btn secondary"
                        onClick={() => setPreviewModal({ show: true, content })}
                      >
                        <i className="material-icons">preview</i>
                        Preview
                      </button>
                      <button 
                        className="teacher-btn secondary"
                        onClick={() => handleEditContent(content)}
                      >
                        <i className="material-icons">edit</i>
                        Edit
                      </button>
                      <button 
                        className="teacher-btn secondary"
                        onClick={() => setAnalyticsModal({ show: true, content })}
                      >
                        <i className="material-icons">analytics</i>
                        Analytics
                      </button>
                      <button 
                        className="teacher-btn secondary"
                        onClick={() => handleDuplicateContent(content)}
                      >
                        <i className="material-icons">content_copy</i>
                        Duplicate
                      </button>
                      {content.status === 'published' ? (
                        <button 
                          className="teacher-btn warning"
                          onClick={() => handleUnpublishContent(content.id)}
                        >
                          <i className="material-icons">unpublish</i>
                          Unpublish
                        </button>
                      ) : (
                        <button 
                          className="teacher-btn success"
                          onClick={() => handlePublishContent(content.id)}
                        >
                          <i className="material-icons">publish</i>
                          Publish
                        </button>
                      )}
                      <button 
                        className="teacher-btn error"
                        onClick={() => handleDeleteContent(content.id)}
                      >
                        <i className="material-icons">delete</i>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="teacher-analytics-section">
            <div className="teacher-analytics-grid">
              <div className="teacher-analytics-card">
                <h3>Content Performance</h3>
                <div className="teacher-chart-placeholder">
                  <i className="material-icons">bar_chart</i>
                  <p>Performance charts will be displayed here</p>
                </div>
              </div>
              
              <div className="teacher-analytics-card">
                <h3>Revenue Trends</h3>
                <div className="teacher-chart-placeholder">
                  <i className="material-icons">trending_up</i>
                  <p>Revenue charts will be displayed here</p>
                </div>
              </div>
              
              <div className="teacher-analytics-card">
                <h3>Student Engagement</h3>
                <div className="teacher-chart-placeholder">
                  <i className="material-icons">people</i>
                  <p>Engagement metrics will be displayed here</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <div className="teacher-templates-section">
            <div className="teacher-section-header">
              <div className="teacher-section-title">
                <i className="material-icons">content_copy</i>
                Content Templates
              </div>
              <p>Use pre-built templates to create content faster</p>
            </div>
            
            <div className="teacher-templates-grid">
              <div className="teacher-template-card">
                <div className="template-icon">
                  <i className="material-icons">school</i>
                </div>
                <h3>Course Template</h3>
                <p>Complete course structure with modules, lessons, and assessments</p>
                <div className="template-meta">
                  <span className="template-type">Course</span>
                  <span className="template-difficulty">All Levels</span>
                </div>
                <div className="template-actions">
                  <button className="teacher-btn primary">Use Template</button>
                  <button className="teacher-btn secondary">Preview</button>
                </div>
              </div>
              
              <div className="teacher-template-card">
                <div className="template-icon">
                  <i className="material-icons">video_library</i>
                </div>
                <h3>Video Lesson Template</h3>
                <p>Structured video lesson with introduction, content, and summary</p>
                <div className="template-meta">
                  <span className="template-type">Video</span>
                  <span className="template-difficulty">All Levels</span>
                </div>
                <div className="template-actions">
                  <button className="teacher-btn primary">Use Template</button>
                  <button className="teacher-btn secondary">Preview</button>
                </div>
              </div>
              
              <div className="teacher-template-card">
                <div className="template-icon">
                  <i className="material-icons">quiz</i>
                </div>
                <h3>Assessment Template</h3>
                <p>Quiz and test template with multiple question types</p>
                <div className="template-meta">
                  <span className="template-type">Assessment</span>
                  <span className="template-difficulty">All Levels</span>
                </div>
                <div className="template-actions">
                  <button className="teacher-btn primary">Use Template</button>
                  <button className="teacher-btn secondary">Preview</button>
                </div>
              </div>
              
              <div className="teacher-template-card">
                <div className="template-icon">
                  <i className="material-icons">article</i>
                </div>
                <h3>Article Template</h3>
                <p>Written content template with sections and formatting</p>
                <div className="template-meta">
                  <span className="template-type">Article</span>
                  <span className="template-difficulty">All Levels</span>
                </div>
                <div className="template-actions">
                  <button className="teacher-btn primary">Use Template</button>
                  <button className="teacher-btn secondary">Preview</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Resources Tab */}
        {activeTab === 'resources' && (
          <div className="teacher-resources-section">
            <div className="teacher-section-header">
              <div className="teacher-section-title">
                <i className="material-icons">folder</i>
                Teaching Resources
              </div>
              <p>Access educational materials and tools to enhance your content</p>
            </div>
            
            <div className="teacher-resources-grid">
              <div className="teacher-resource-card">
                <div className="resource-icon">
                  <i className="material-icons">image</i>
                </div>
                <h3>Stock Images</h3>
                <p>High-quality images for your course materials</p>
                <div className="resource-meta">
                  <span className="resource-type">Images</span>
                  <span className="resource-count">10,000+</span>
                </div>
                <div className="resource-actions">
                  <button className="teacher-btn primary">Browse</button>
                  <button className="teacher-btn secondary">Search</button>
                </div>
              </div>
              
              <div className="teacher-resource-card">
                <div className="resource-icon">
                  <i className="material-icons">video_library</i>
                </div>
                <h3>Video Assets</h3>
                <p>Educational videos and animations</p>
                <div className="resource-meta">
                  <span className="resource-type">Videos</span>
                  <span className="resource-count">5,000+</span>
                </div>
                <div className="resource-actions">
                  <button className="teacher-btn primary">Browse</button>
                  <button className="teacher-btn secondary">Search</button>
                </div>
              </div>
              
              <div className="teacher-resource-card">
                <div className="resource-icon">
                  <i className="material-icons">description</i>
                </div>
                <h3>Documents</h3>
                <p>PDFs, worksheets, and printable materials</p>
                <div className="resource-meta">
                  <span className="resource-type">Documents</span>
                  <span className="resource-count">2,000+</span>
                </div>
                <div className="resource-actions">
                  <button className="teacher-btn primary">Browse</button>
                  <button className="teacher-btn secondary">Search</button>
                </div>
              </div>
              
              <div className="teacher-resource-card">
                <div className="resource-icon">
                  <i className="material-icons">audio_file</i>
                </div>
                <h3>Audio Files</h3>
                <p>Pronunciation guides and audio lessons</p>
                <div className="resource-meta">
                  <span className="resource-type">Audio</span>
                  <span className="resource-count">1,500+</span>
                </div>
                <div className="resource-actions">
                  <button className="teacher-btn primary">Browse</button>
                  <button className="teacher-btn secondary">Search</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="teacher-settings-section">
            <div className="teacher-settings-card">
              <h3>Content Settings</h3>
              <div className="teacher-setting-item">
                <label>Default Language</label>
                <select className="teacher-select">
                  <option value="english">English</option>
                  <option value="spanish">Spanish</option>
                  <option value="french">French</option>
                </select>
              </div>
              
              <div className="teacher-setting-item">
                <label>Default Difficulty</label>
                <select className="teacher-select">
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>
              
              <div className="teacher-setting-item">
                <label>Auto-publish new content</label>
                <input type="checkbox" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {uploadModal && (
        <div className="teacher-modal-overlay">
          <div className="teacher-modal">
            <div className="teacher-modal-header">
              <h2>Upload New Content</h2>
              <button 
                className="teacher-modal-close"
                onClick={() => setUploadModal(false)}
              >
                <i className="material-icons">close</i>
              </button>
            </div>
            
            <form onSubmit={handleUploadSubmit} className="teacher-upload-form">
              <div className="teacher-form-grid">
                <div className="teacher-form-group">
                  <label>Content Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={uploadForm.title}
                    onChange={handleUploadInputChange}
                    required
                    placeholder="Enter content title"
                  />
                </div>

                <div className="teacher-form-group">
                  <label>Category *</label>
                  <select
                    name="category"
                    value={uploadForm.category}
                    onChange={handleUploadInputChange}
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                <div className="teacher-form-group">
                  <label>Difficulty Level *</label>
                  <select
                    name="difficulty"
                    value={uploadForm.difficulty}
                    onChange={handleUploadInputChange}
                    required
                  >
                    {difficulties.map(difficulty => (
                      <option key={difficulty} value={difficulty}>
                        {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="teacher-form-group">
                  <label>Language *</label>
                  <select
                    name="language"
                    value={uploadForm.language}
                    onChange={handleUploadInputChange}
                    required
                  >
                    {languages.map(language => (
                      <option key={language} value={language}>
                        {language.charAt(0).toUpperCase() + language.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="teacher-form-group full-width">
                  <label>Description *</label>
                  <textarea
                    name="description"
                    value={uploadForm.description}
                    onChange={handleUploadInputChange}
                    required
                    placeholder="Describe your content..."
                    rows="4"
                  />
                </div>

                <div className="teacher-form-group">
                  <label>Duration</label>
                  <input
                    type="text"
                    name="duration"
                    value={uploadForm.duration}
                    onChange={handleUploadInputChange}
                    placeholder="e.g., 2 hours, 30 minutes"
                  />
                </div>

                <div className="teacher-form-group">
                  <label>Tags</label>
                  <input
                    type="text"
                    name="tags"
                    value={uploadForm.tags}
                    onChange={handleUploadInputChange}
                    placeholder="Enter tags separated by commas"
                  />
                </div>

                <div className="teacher-form-group">
                  <label>Price</label>
                  <div className="teacher-price-input">
                    <input
                      type="number"
                      name="price"
                      value={uploadForm.price}
                      onChange={handleUploadInputChange}
                      min="0"
                      step="0.01"
                      disabled={uploadForm.isFree}
                    />
                    <label className="teacher-checkbox-label">
                      <input
                        type="checkbox"
                        name="isFree"
                        checked={uploadForm.isFree}
                        onChange={handleUploadInputChange}
                      />
                      Free
                    </label>
                  </div>
                </div>

                <div className="teacher-form-group">
                  <label>Thumbnail</label>
                  <input
                    type="file"
                    name="thumbnail"
                    accept="image/*"
                    onChange={handleUploadInputChange}
                  />
                </div>

                <div className="teacher-form-group full-width">
                  <label>Content Files</label>
                  <input
                    type="file"
                    name="contentFiles"
                    multiple
                    onChange={handleUploadInputChange}
                  />
                </div>

                <div className="teacher-form-group">
                  <label>Video URL</label>
                  <input
                    type="url"
                    name="videoUrl"
                    value={uploadForm.videoUrl}
                    onChange={handleUploadInputChange}
                    placeholder="YouTube, Vimeo, or direct video URL"
                  />
                </div>

                <div className="teacher-form-group">
                  <label>Audio URL</label>
                  <input
                    type="url"
                    name="audioUrl"
                    value={uploadForm.audioUrl}
                    onChange={handleUploadInputChange}
                    placeholder="Audio file URL"
                  />
                </div>

                <div className="teacher-form-group">
                  <label>PDF URL</label>
                  <input
                    type="url"
                    name="pdfUrl"
                    value={uploadForm.pdfUrl}
                    onChange={handleUploadInputChange}
                    placeholder="PDF file URL"
                  />
                </div>

                <div className="teacher-form-group full-width">
                  <label>Learning Objectives</label>
                  <textarea
                    name="learningObjectives"
                    value={uploadForm.learningObjectives}
                    onChange={handleUploadInputChange}
                    placeholder="What will students learn from this content?"
                    rows="3"
                  />
                </div>

                <div className="teacher-form-group full-width">
                  <label>Prerequisites</label>
                  <textarea
                    name="prerequisites"
                    value={uploadForm.prerequisites}
                    onChange={handleUploadInputChange}
                    placeholder="What should students know before taking this content?"
                    rows="2"
                  />
                </div>

                <div className="teacher-form-group full-width">
                  <label>Target Audience</label>
                  <textarea
                    name="targetAudience"
                    value={uploadForm.targetAudience}
                    onChange={handleUploadInputChange}
                    placeholder="Who is this content designed for?"
                    rows="2"
                  />
                </div>

                <div className="teacher-form-group">
                  <label className="teacher-checkbox-label">
                    <input
                      type="checkbox"
                      name="certificate"
                      checked={uploadForm.certificate}
                      onChange={handleUploadInputChange}
                    />
                    Offer Certificate
                  </label>
                </div>

                {uploadForm.certificate && (
                  <div className="teacher-form-group">
                    <label>Certificate Name</label>
                    <input
                      type="text"
                      name="certificateName"
                      value={uploadForm.certificateName}
                      onChange={handleUploadInputChange}
                      placeholder="Certificate of Completion"
                    />
                  </div>
                )}

                <div className="teacher-form-group">
                  <label>Maximum Students</label>
                  <input
                    type="number"
                    name="maxStudents"
                    value={uploadForm.maxStudents}
                    onChange={handleUploadInputChange}
                    min="0"
                    placeholder="0 for unlimited"
                  />
                </div>

                <div className="teacher-form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    name="startDate"
                    value={uploadForm.startDate}
                    onChange={handleUploadInputChange}
                  />
                </div>

                <div className="teacher-form-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    name="endDate"
                    value={uploadForm.endDate}
                    onChange={handleUploadInputChange}
                  />
                </div>

                <div className="teacher-form-group">
                  <label className="teacher-checkbox-label">
                    <input
                      type="checkbox"
                      name="isPublished"
                      checked={uploadForm.isPublished}
                      onChange={handleUploadInputChange}
                    />
                    Publish immediately
                  </label>
                </div>

                <div className="teacher-form-group">
                  <label className="teacher-checkbox-label">
                    <input
                      type="checkbox"
                      name="isFeatured"
                      checked={uploadForm.isFeatured}
                      onChange={handleUploadInputChange}
                    />
                    Feature this content
                  </label>
                </div>
              </div>

              <div className="teacher-modal-actions">
                <button 
                  type="button" 
                  className="teacher-btn secondary"
                  onClick={() => setUploadModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="teacher-btn primary"
                >
                  <i className="material-icons">upload</i>
                  Upload Content
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewModal.show && (
        <div className="teacher-modal-overlay">
          <div className="teacher-modal large">
            <div className="teacher-modal-header">
              <h2>Content Preview</h2>
              <button 
                className="teacher-modal-close"
                onClick={() => setPreviewModal({ show: false, content: null })}
              >
                <i className="material-icons">close</i>
              </button>
            </div>
            
            <div className="teacher-preview-content">
              <div className="teacher-preview-header">
                <img src={previewModal.content.thumbnail || '/default-thumbnail.jpg'} alt={previewModal.content.title} />
                <div className="teacher-preview-info">
                  <h3>{previewModal.content.title}</h3>
                  <p>{previewModal.content.description}</p>
                  <div className="teacher-preview-meta">
                    <span className="teacher-preview-category">{previewModal.content.category}</span>
                    <span className={`teacher-preview-difficulty ${getDifficultyColor(previewModal.content.difficulty)}`}>
                      {previewModal.content.difficulty}
                    </span>
                    <span className="teacher-preview-duration">{previewModal.content.duration}</span>
                  </div>
                </div>
              </div>
              
              <div className="teacher-preview-placeholder">
                <i className="material-icons">play_circle</i>
                <p>Content preview will be displayed here</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Analytics Modal */}
      {analyticsModal.show && (
        <div className="teacher-modal-overlay">
          <div className="teacher-modal large">
            <div className="teacher-modal-header">
              <h2>Content Analytics</h2>
              <button 
                className="teacher-modal-close"
                onClick={() => setAnalyticsModal({ show: false, content: null })}
              >
                <i className="material-icons">close</i>
              </button>
            </div>
            
            <div className="teacher-analytics-content">
              {analyticsModal.content ? (
                <div className="teacher-content-analytics">
                  <h3>{analyticsModal.content.title}</h3>
                  <div className="teacher-analytics-grid">
                    <div className="teacher-analytics-metric">
                      <span className="teacher-metric-value">{analyticsModal.content.views}</span>
                      <span className="teacher-metric-label">Total Views</span>
                    </div>
                    <div className="teacher-analytics-metric">
                      <span className="teacher-metric-value">{analyticsModal.content.enrollments}</span>
                      <span className="teacher-metric-label">Enrollments</span>
                    </div>
                    <div className="teacher-analytics-metric">
                      <span className="teacher-metric-value">{analyticsModal.content.rating}</span>
                      <span className="teacher-metric-label">Rating</span>
                    </div>
                    <div className="teacher-analytics-metric">
                      <span className="teacher-metric-value">${analyticsModal.content.revenue}</span>
                      <span className="teacher-metric-label">Revenue</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="teacher-overall-analytics">
                  <h3>Overall Performance</h3>
                  <div className="teacher-analytics-grid">
                    <div className="teacher-analytics-metric">
                      <span className="teacher-metric-value">{analytics.totalContent}</span>
                      <span className="teacher-metric-label">Total Content</span>
                    </div>
                    <div className="teacher-analytics-metric">
                      <span className="teacher-metric-value">{analytics.totalViews.toLocaleString()}</span>
                      <span className="teacher-metric-label">Total Views</span>
                    </div>
                    <div className="teacher-analytics-metric">
                      <span className="teacher-metric-value">{analytics.totalEnrollments}</span>
                      <span className="teacher-metric-label">Total Enrollments</span>
                    </div>
                    <div className="teacher-analytics-metric">
                      <span className="teacher-metric-value">${analytics.totalRevenue.toLocaleString()}</span>
                      <span className="teacher-metric-label">Total Revenue</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherSectionCourseLibraryManagement; 